import React, { useState, useEffect } from 'react'
    import { motion, AnimatePresence } from 'framer-motion'
    import { useFamily } from '../../contexts/FamilyContext'
    import { useKiosk } from '../../contexts/KioskContext'
    import { useCelebrationContext } from '../../contexts/CelebrationContext'
    import SafeIcon from '../../common/SafeIcon'
    import * as FiIcons from 'react-icons/fi'
    import Button from '../ui/Button'
    import Card from '../ui/Card'
    import Badge from '../ui/Badge'
    import ProgressBar from '../ui/ProgressBar'
    import Avatar from '../ui/Avatar'
    import toast from 'react-hot-toast'

    const { FiStar, FiGift, FiTrophy, FiPlus, FiCheck, FiClock, FiUsers, FiLogOut, FiRefreshCw } = FiIcons

    const KioskChildView = ({ child, onSwitchChild, onExitKiosk }) => {
      const { tasks, rewards, getChildPoints, getChildLevel, completions, completeTask, updateTask } = useFamily()
      const { kioskSettings, updateActivity } = useKiosk()
      const { celebrateTaskComplete } = useCelebrationContext()
      const [activeTab, setActiveTab] = useState('tasks')
      const [showRewardRequest, setShowRewardRequest] = useState(false)
      const [selectedReward, setSelectedReward] = useState(null)
      const [pendingRequests, setPendingRequests] = useState([])

      const childPoints = getChildPoints(child.id)
      const level = getChildLevel(childPoints)

      // Get available tasks (unassigned or assigned to this child)
      const availableTasks = tasks.filter(task => 
        !task.assigned_to || task.assigned_to === child.id
      )

      // Get unassigned tasks that can be self-assigned
      const unassignedTasks = tasks.filter(task => 
        !task.assigned_to && kioskSettings.allowTaskSelfAssignment
      )

      // Get today's completions for this child
      const todayCompletions = completions.filter(c => {
        const today = new Date().toDateString()
        return c.child_id === child.id && new Date(c.completed_at).toDateString() === today
      })

      // Get affordable rewards
      const affordableRewards = rewards.filter(reward => 
        childPoints >= reward.points_required
      )

      useEffect(() => {
        // Update activity when component mounts or child changes
        updateActivity()
      }, [child.id])

      const handleCompleteTask = async (task) => {
        updateActivity()
        const result = await completeTask(task.id, child.id)
        if (result && !result.error) {
          // Pass the actual points earned to the celebration
          celebrateTaskComplete(child.avatar || '👦', task.title, task.points)
          toast.success(`Great job! +${task.points} points! 🌟`, { duration: 3000 })
        }
      }

      const handleSelfAssignTask = async (task) => {
        updateActivity()
        const result = await updateTask(task.id, { ...task, assigned_to: child.id })
        if (result && !result.error) {
          toast.success(`Task assigned to you! Now you can complete it! 📝`)
        }
      }

      const handleRequestReward = (reward) => {
        updateActivity()
        if (!kioskSettings.allowRewardRequests) {
          toast.error('Reward requests are not allowed in kiosk mode')
          return
        }
        setSelectedReward(reward)
        setShowRewardRequest(true)
      }

      const submitRewardRequest = () => {
        if (!selectedReward) return

        const newRequest = {
          id: Date.now(),
          childId: child.id,
          childName: child.name,
          reward: selectedReward,
          requestedAt: new Date(),
          status: 'pending'
        }

        setPendingRequests(prev => [...prev, newRequest])

        // Store in localStorage for parent to see
        const existingRequests = JSON.parse(localStorage.getItem('kiosk_reward_requests') || '[]')
        localStorage.setItem('kiosk_reward_requests', JSON.stringify([...existingRequests, newRequest]))

        setShowRewardRequest(false)
        setSelectedReward(null)
        toast.success(`Reward request sent! Ask your parents to check. 📨`)
      }

      const getTaskIcon = (category) => {
        const icons = {
          'cleaning': '🧹',
          'dishes': '🍽️',
          'laundry': '👕',
          'pets': '🐕',
          'homework': '📚',
          'outdoor': '🌱',
          'cooking': '👨‍🍳',
          'organization': '📦',
          'seasonal': '🎄',
          'technology': '💻',
          'other': '⭐'
        }
        return icons[category] || '⭐'
      }

      const formatTimeDisplay = (minutes) => {
        if (!minutes) return null
        if (minutes < 60) {
          return `${minutes} min`
        }
        const hours = Math.floor(minutes / 60)
        const mins = minutes % 60
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-highlight-50 via-accent-50 to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700">
          {/* Header */}
          <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
            <div className="max-w-7xl mx-auto px-4 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar name={child.name} emoji={child.avatar || '👦'} size="lg" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      Welcome, {child.name}! 👋
                    </h1>
                    <div className="flex items-center gap-2">
                      <Badge variant="primary" className="text-xs">
                        Level {level.level}: {level.title}
                      </Badge>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {childPoints} points
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onSwitchChild}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                  >
                    <SafeIcon icon={FiUsers} />
                    Switch Child
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onExitKiosk}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                  >
                    <SafeIcon icon={FiLogOut} />
                    Exit
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Progress Section */}
            <Card className="mb-6 bg-gradient-to-r from-primary-500 to-secondary-500 text-white border-none">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold">{childPoints}</div>
                  <div className="text-white/80">Total Points</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{todayCompletions.length}</div>
                  <div className="text-white/80">Completed Today</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{affordableRewards.length}</div>
                  <div className="text-white/80">Rewards Available</div>
                </div>
              </div>
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/90">Progress to Level {level.level + 1}</span>
                  <span className="text-white/90">{level.next - childPoints} more points</span>
                </div>
                <ProgressBar 
                  value={childPoints} 
                  max={level.next} 
                  showLabel={false} 
                  color="accent" 
                />
              </div>
            </Card>

            {/* Navigation Tabs */}
            <div className="flex justify-center mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-2 flex gap-2 border border-gray-200 dark:border-gray-700">
                {[
                  { id: 'tasks', label: 'My Tasks', icon: FiStar, count: availableTasks.length },
                  { id: 'assign', label: 'Get Tasks', icon: FiPlus, count: unassignedTasks.length },
                  { id: 'rewards', label: 'Rewards', icon: FiGift, count: affordableRewards.length }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id)
                      updateActivity()
                    }}
                    className={`
                      flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200
                      ${activeTab === tab.id
                        ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-md'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }
                    `}
                  >
                    <SafeIcon icon={tab.icon} />
                    <span>{tab.label}</span>
                    {tab.count > 0 && (
                      <Badge 
                        variant={activeTab === tab.id ? "secondary" : "primary"} 
                        size="sm" 
                        className="ml-1"
                      >
                        {tab.count}
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* My Tasks Tab */}
                {activeTab === 'tasks' && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
                      🎯 Your Tasks to Complete
                    </h2>

                    {availableTasks.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {availableTasks.map((task, index) => (
                          <motion.div
                            key={task.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.1 }}
                            whileHover={{ scale: 1.02, y: -5 }}
                          >
                            <Card className="h-full hover:shadow-xl transition-all duration-300 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                              <div className="text-center">
                                <div className="text-4xl mb-3">
                                  {getTaskIcon(task.category)}
                                </div>
                                <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100 mb-2">
                                  {task.title}
                                </h3>
                                {task.description && (
                                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                                    {task.description}
                                  </p>
                                )}
                                <div className="flex items-center justify-center gap-2 mb-3">
                                  <Badge variant="primary" className="flex items-center gap-1">
                                    <SafeIcon icon={FiStar} className="text-sm" />
                                    {task.points} points
                                  </Badge>
                                  {task.estimated_time_minutes && (
                                    <Badge variant="secondary" className="flex items-center gap-1">
                                      <SafeIcon icon={FiClock} className="text-xs" />
                                      {formatTimeDisplay(task.estimated_time_minutes)}
                                    </Badge>
                                  )}
                                </div>
                                <motion.div
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <Button
                                    variant="success"
                                    className="w-full"
                                    onClick={() => handleCompleteTask(task)}
                                  >
                                    <SafeIcon icon={FiCheck} />
                                    Complete Task!
                                  </Button>
                                </motion.div>
                              </div>
                            </Card>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <Card className="text-center py-12 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                        <div className="text-6xl mb-4">🎉</div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          All Done!
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          Great job! You've completed all your tasks. Check back later for more!
                        </p>
                      </Card>
                    )}
                  </div>
                )}

                {/* Get Tasks Tab */}
                {activeTab === 'assign' && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
                      📝 Choose Tasks to Do
                    </h2>

                    {kioskSettings.allowTaskSelfAssignment ? (
                      unassignedTasks.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {unassignedTasks.map((task, index) => (
                            <motion.div
                              key={task.id}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.1 }}
                              whileHover={{ scale: 1.02, y: -5 }}
                            >
                              <Card className="h-full hover:shadow-xl transition-all duration-300 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                                <div className="text-center">
                                  <div className="text-4xl mb-3">
                                    {getTaskIcon(task.category)}
                                  </div>
                                  <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100 mb-2">
                                    {task.title}
                                  </h3>
                                  {task.description && (
                                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                                      {task.description}
                                    </p>
                                  )}
                                  <div className="flex items-center justify-center gap-2 mb-3">
                                    <Badge variant="primary" className="flex items-center gap-1">
                                      <SafeIcon icon={FiStar} className="text-sm" />
                                      {task.points} points
                                    </Badge>
                                    {task.estimated_time_minutes && (
                                      <Badge variant="secondary" className="flex items-center gap-1">
                                        <SafeIcon icon={FiClock} className="text-xs" />
                                        {formatTimeDisplay(task.estimated_time_minutes)}
                                      </Badge>
                                    )}
                                  </div>
                                  <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    <Button
                                      variant="primary"
                                      className="w-full"
                                      onClick={() => handleSelfAssignTask(task)}
                                    >
                                      <SafeIcon icon={FiPlus} />
                                      I'll Do This!
                                    </Button>
                                  </motion.div>
                                </div>
                              </Card>
                            </motion.div>
                          ))}
                        </div>
                      ) : (
                        <Card className="text-center py-12 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                          <div className="text-6xl mb-4">✨</div>
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            No Available Tasks
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400">
                            All tasks are already assigned. Check your "My Tasks" tab!
                          </p>
                        </Card>
                      )
                    ) : (
                      <Card className="text-center py-12 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                        <div className="text-6xl mb-4">🔒</div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          Task Assignment Disabled
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          Ask your parents to enable task self-assignment in kiosk settings.
                        </p>
                      </Card>
                    )}
                  </div>
                )}

                {/* Rewards Tab */}
                {activeTab === 'rewards' && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
                      🎁 Awesome Rewards
                    </h2>

                    {rewards.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {rewards.map((reward, index) => {
                          const canAfford = childPoints >= reward.points_required

                          return (
                            <motion.div
                              key={reward.id}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.1 }}
                              whileHover={{ 
                                scale: canAfford ? 1.02 : 1, 
                                y: canAfford ? -5 : 0 
                              }}
                            >
                              <Card className={`
                                h-full transition-all duration-300 border-gray-200 dark:border-gray-700
                                ${canAfford 
                                  ? 'hover:shadow-xl border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20' 
                                  : 'opacity-75 bg-white dark:bg-gray-800'
                                }
                              `}>
                                <div className="text-center">
                                  <div className="text-4xl mb-3">
                                    {reward.emoji || '🎁'}
                                  </div>
                                  <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100 mb-2">
                                    {reward.title}
                                  </h3>
                                  {reward.description && (
                                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                                      {reward.description}
                                    </p>
                                  )}
                                  <div className="mb-4">
                                    <Badge 
                                      variant={canAfford ? "success" : "gray"} 
                                      className="flex items-center gap-1 mx-auto w-fit"
                                    >
                                      <SafeIcon icon={FiStar} className="text-sm" />
                                      {reward.points_required} points
                                    </Badge>
                                  </div>
                                  {!canAfford && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                      Need {reward.points_required - childPoints} more points
                                    </p>
                                  )}
                                  <motion.div
                                    whileHover={canAfford ? { scale: 1.05 } : {}}
                                    whileTap={canAfford ? { scale: 0.95 } : {}}
                                  >
                                    <Button
                                      variant={canAfford ? "success" : "outline"}
                                      className="w-full"
                                      onClick={() => handleRequestReward(reward)}
                                      disabled={!canAfford || !kioskSettings.allowRewardRequests}
                                    >
                                      <SafeIcon icon={FiGift} />
                                      {canAfford 
                                        ? (kioskSettings.allowRewardRequests ? 'Request Reward!' : 'Ask Parents')
                                        : 'Not Enough Points'
                                      }
                                    </Button>
                                  </motion.div>
                                </div>
                              </Card>
                            </motion.div>
                          )
                        })}
                      </div>
                    ) : (
                      <Card className="text-center py-12 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                        <div className="text-6xl mb-4">🎁</div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          No Rewards Yet
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          Ask your parents to add some exciting rewards!
                        </p>
                      </Card>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Today's Achievements */}
            {todayCompletions.length > 0 && (
              <Card className="mt-8 bg-gradient-to-r from-success-500 to-highlight-600 text-white border-none">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiTrophy} />
                  Today's Achievements
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {todayCompletions.map((completion, index) => (
                    <motion.div
                      key={completion.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white/20 rounded-lg p-4 text-center"
                    >
                      <div className="text-2xl mb-2">🎉</div>
                      <div className="font-medium text-sm">
                        {completion.tasks?.title}
                      </div>
                      <div className="text-xs opacity-90">
                        +{completion.points_earned} points
                      </div>
                    </motion.div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Reward Request Modal */}
          {showRewardRequest && selectedReward && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md border border-gray-200 dark:border-gray-700"
              >
                <div className="p-6 text-center">
                  <div className="text-6xl mb-4">{selectedReward.emoji || '🎁'}</div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    Request {selectedReward.title}?
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    This will send a request to your parents to redeem this reward for {selectedReward.points_required} points.
                  </p>
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg mb-6 border border-yellow-200 dark:border-yellow-800">
                    <p className="text-sm text-yellow-800 dark:text-yellow-300">
                      Your parents will need to approve this request before you get your reward!
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="ghost"
                      onClick={() => setShowRewardRequest(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="success"
                      onClick={submitRewardRequest}
                      className="flex-1"
                    >
                      <SafeIcon icon={FiGift} />
                      Send Request
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      )
    }

    export default KioskChildView